import { useEffect, useState } from "react";
import { callErrorToast } from "../../../utilities/utilities.ts";
import { AttachmentsService } from "../../../services/registry/attachments.ts";
import { Attachment } from "../../../Models/Attachment.ts";
import { defaultFetchParam } from "../../../Models/FetchParam.ts";
import { createModalConfig, ModalConfig } from "../../../Models/ModalConfig.ts";
import {
  AttachmentsIndexContextValue,
  AttachmentsIndexProviderProps,
} from "./types.ts";
import { AttachmentsIndexContext } from "./contexts.ts";

const attachmentsService = new AttachmentsService();

/* ===========================================================================
 * PROVIDER: AttachmentIndexProvider
 * ===========================================================================
 */

/**
 * @component AttachmentIndexProvider
 *
 * Centralizes state management and data retrieval logic for Attachment entities
 * associated with a specific parent resource (e.g., shipment).
 *
 * Responsibilities:
 * - Fetch attachments filtered by shipmentId
 * - Manage loading state
 * - Handle modal configuration state
 * - Expose data and utilities via Context
 *
 * Must be consumed through the corresponding context hook.
 */
export const AttachmentIndexProvider: React.FC<
  AttachmentsIndexProviderProps
> = ({ id, children, callback }) => {
  /** Local state containing the list of attachments */
  const [data, setData] = useState<Partial<Attachment[]>>([]);

  /** Indicates loading state for async operations */
  const [loader, setLoader] = useState<boolean>(true);

  /** Modal configuration state */
  const [modalConfig, setModalConfig] =
    useState<ModalConfig>(createModalConfig());

  /* ---------------------------------------------------------------------------
   * FUNCTION: getAttachment
   * Fetch attachments filtered by shipmentId.
   * ------------------------------------------------------------------------- */

  /**
   * Retrieves all attachments associated with the provided shipment ID.
   *
   * @async
   * @param {string} id - The shipment ID used for filtering.
   * @returns {Promise<any>} The service response.
   */
  const getAttachment = async (id: string) => {
    setLoader(true);
    try {
      const res = await attachmentsService.all({
        ...defaultFetchParam,
        search: [
          {
            selector: "shipmentId",
            value: id,
            precise: true,
          },
        ],
      });

      setData(res.data.content || []);
      return res;
    } catch (err) {
      callErrorToast(err);
      throw err;
    } finally {
      setLoader(false);
    }
  };

  /* ---------------------------------------------------------------------------
   * EFFECTS
   * ------------------------------------------------------------------------- */

  /**
   * Automatically fetches attachments when the ID changes.
   */
  useEffect(() => {
    if (id) {
      getAttachment(id);
      return;
    }
    setLoader(false);
  }, [id]);

  /* ---------------------------------------------------------------------------
   * PROVIDER RETURN
   * ------------------------------------------------------------------------- */

  const contextValue: AttachmentsIndexContextValue = {
    data,
    loader,
    modalConfig,
    setModalConfig,
    callback,
  };

  return (
    <AttachmentsIndexContext.Provider value={contextValue}>
      {children}
    </AttachmentsIndexContext.Provider>
  );
};
